import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartErrorEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const MINI_CART_REC_ADD_EVENT = 'mini-cart-product-recommendation';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  static #wishlistActionSelector = '.cart-items__actions';
  static #wishlistElementSelector = 'save-for-later';

  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #debouncedHandleRecommendationAddToCart = debounce(
    this.#handleRecommendationAddToCart,
    300,
  ).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(ThemeEvents.miniCartRecAdd, this.#debouncedHandleRecommendationAddToCart);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(ThemeEvents.miniCartRecAdd, this.#debouncedHandleRecommendationAddToCart);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      nestedLines: this.#getNestedLines(lineItemRow),
    });

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      this.#updateDrawerEmptyState();

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Returns the string lines controlled by a racket row.
   * @param {HTMLTableRowElement} parentRow
   * @returns {Array<{line: number, quantity: number}>}
   */
  #getNestedLines(parentRow) {
    const parentKey = parentRow.dataset.key;
    if (!parentKey) throw new Error('Cart line key not found');

    return this.refs.cartItemRows.flatMap((row, index) => {
      if (row.dataset.parentKey !== parentKey || !row.classList.contains('cart-items__nested-line')) return [];

      const quantity = Number(row.dataset.quantity);
      if (!Number.isInteger(quantity) || quantity < 1) throw new Error('Nested string quantity not found');

      return [{ line: index + 1, quantity }];
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {Array<{line: number, quantity: number}>} [config.nestedLines] - Nested string lines controlled by the parent line.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const wishlistMarkupByLineKey = this.#captureWishlistMarkup();

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    document.querySelectorAll('[data-cart-refresh-section-id]').forEach((marker) => {
      if (!(marker instanceof HTMLElement)) return;
      const sectionId = marker.dataset.cartRefreshSectionId;
      if (sectionId) sectionsToUpdate.add(sectionId);
    });

    cartTotal?.shimmer();
    const updatedNestedLines = [];

    try {
      for (const nestedLine of config.nestedLines ?? []) {
        const nestedResponse = await this.#requestQuantityChange({
          line: nestedLine.line,
          quantity,
        });
        const nestedError = this.#getCartError(nestedResponse);
        if (nestedError) {
          await this.#restoreNestedLineQuantities(updatedNestedLines);
          resetShimmer(this);
          this.#handleCartError(line, { errors: nestedError });
          return;
        }
        updatedNestedLines.push(nestedLine);
      }

      const parsedResponseText = await this.#requestQuantityChange({
        line,
        quantity,
        sections: Array.from(sectionsToUpdate).join(','),
      });

      resetShimmer(this);

      const cartError = this.#getCartError(parsedResponseText);
      if (cartError) {
        await this.#restoreNestedLineQuantities(updatedNestedLines);
        this.#handleCartError(line, { errors: cartError });
        return;
      }
      updatedNestedLines.length = 0;

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full', { injectStylesheet: true });
      this.#restoreWishlistMarkup(wishlistMarkupByLineKey);
      this.#emitCartDomUpdated();

      if (newCartItemCount === 0 && this.isDrawer) {
        this.#updateDrawerEmptyState();
      }

      Object.entries(parsedResponseText.sections || {}).forEach(([sectionId, html]) => {
        if (sectionId === this.sectionId) return;
        try {
          morphSection(sectionId, html, 'full', { injectStylesheet: true });
        } catch {
          // Ignore sections not present on this page.
        }
      });

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      try {
        await this.#restoreNestedLineQuantities(updatedNestedLines);
      } catch (restoreError) {
        console.error(restoreError);
      }
      resetShimmer(this);
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Changes one cart line while preserving Shopify's inventory validation.
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} [config.sections]
   * @returns {Promise<Record<string, any>>}
   */
  async #requestQuantityChange({ line, quantity, sections }) {
    const body = JSON.stringify({
      line,
      quantity,
      ...(sections ? { sections, sections_url: window.location.pathname } : {}),
    });
    const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
    return JSON.parse(await response.text());
  }

  /**
   * Restores nested string quantities after a parent update fails.
   * @param {Array<{line: number, quantity: number}>} nestedLines
   */
  async #restoreNestedLineQuantities(nestedLines) {
    for (const nestedLine of [...nestedLines].reverse()) {
      const response = await this.#requestQuantityChange({
        line: nestedLine.line,
        quantity: nestedLine.quantity,
      });
      const cartError = this.#getCartError(response);
      if (cartError) throw new Error(cartError);
    }
  }

  /**
   * @param {Record<string, any>} response
   * @returns {string}
   */
  #getCartError(response) {
    const error = response.errors ?? response.description ?? response.message;
    if (!error) return '';
    return typeof error === 'string' ? error : JSON.stringify(error);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Re-renders the cart markup after a back/forward restore.
   *
   * A restored page shows exactly the markup it rendered with, and `cartUpdate` events fired on
   * other pages never reach it — so adding from a PDP and pressing Back leaves the mini bag
   * showing the pre-add cart. Only runs on a restore, so normal loads cost nothing.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    const wishlistMarkupByLineKey = this.#captureWishlistMarkup();

    sectionRenderer
      .renderSection(this.sectionId, { cache: false })
      .then(() => {
        this.#restoreWishlistMarkup(wishlistMarkupByLineKey);
        this.#emitCartDomUpdated();

        // An emptied cart needs the drawer's empty-state class, not just new markup.
        if (this.isDrawer && !this.querySelector('.cart-items__table-row')) {
          this.#updateDrawerEmptyState();
        }
      })
      .catch(() => {
        // Offline or the section failed to render — leave the restored markup in place.
      });
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    const wishlistMarkupByLineKey = this.#captureWishlistMarkup();
    const isRecommendationAdd = event instanceof CartAddEvent && event?.detail?.data?.source === MINI_CART_REC_ADD_EVENT;

    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#restoreWishlistMarkup(wishlistMarkupByLineKey);
      this.#emitCartDomUpdated();
      return;
    }
    if (event.target === this && !isRecommendationAdd) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, 'full', { injectStylesheet: true });
      this.#restoreWishlistMarkup(wishlistMarkupByLineKey);
      this.#emitCartDomUpdated();

      if (event.detail.data?.itemCount === 0 && this.isDrawer) {
        this.#updateDrawerEmptyState();
      }

      Object.entries(event.detail.data.sections || {}).forEach(([sectionId, html]) => {
        if (sectionId === this.sectionId) return;
        try {
          morphSection(sectionId, html, 'full', { injectStylesheet: true });
        } catch {
          // Ignore sections not present on this page.
        }
      });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#restoreWishlistMarkup(wishlistMarkupByLineKey);
      this.#emitCartDomUpdated();
    }
  };

  /**
   * Captures wishlist app markup inserted into cart action cells, grouped by line item key.
   * This allows us to restore app-injected nodes after cart section morphs.
   * @returns {Map<string, string[]>}
   */
  #captureWishlistMarkup() {
    const wishlistMarkupByLineKey = new Map();
    const rowSelector = `[ref="cartItemRows[]"][data-key]`;

    for (const row of this.querySelectorAll(rowSelector)) {
      if (!(row instanceof HTMLElement)) continue;

      const lineKey = row.dataset.key;
      if (!lineKey) continue;

      const actionCell = row.querySelector(CartItemsComponent.#wishlistActionSelector);
      if (!(actionCell instanceof HTMLElement)) continue;

      const wishlistElements = actionCell.querySelectorAll(CartItemsComponent.#wishlistElementSelector);
      if (!wishlistElements.length) continue;

      const serializedElements = [];
      for (const element of wishlistElements) {
        if (!(element instanceof HTMLElement)) continue;
        serializedElements.push(element.outerHTML);
      }

      if (!serializedElements.length) continue;
      wishlistMarkupByLineKey.set(lineKey, serializedElements);
    }

    return wishlistMarkupByLineKey;
  }

  /**
   * Restores previously captured wishlist markup when the app has not yet re-injected it.
   * @param {Map<string, string[]>} wishlistMarkupByLineKey
   */
  #restoreWishlistMarkup(wishlistMarkupByLineKey) {
    if (!wishlistMarkupByLineKey.size) return;

    const rowSelector = `[ref="cartItemRows[]"][data-key]`;
    for (const row of this.querySelectorAll(rowSelector)) {
      if (!(row instanceof HTMLElement)) continue;

      const lineKey = row.dataset.key;
      if (!lineKey) continue;

      const serializedElements = wishlistMarkupByLineKey.get(lineKey);
      if (!serializedElements?.length) continue;

      const actionCell = row.querySelector(CartItemsComponent.#wishlistActionSelector);
      if (!(actionCell instanceof HTMLElement)) continue;

      const hasWishlistElement = actionCell.querySelector(CartItemsComponent.#wishlistElementSelector);
      if (hasWishlistElement) continue;

      actionCell.insertAdjacentHTML('afterbegin', serializedElements.join(''));
    }
  }

  /**
   * Emits a generic event after cart DOM updates so app scripts can re-run initialization.
   */
  #emitCartDomUpdated() {
    document.dispatchEvent(new CustomEvent('cart:dom-updated', { detail: { sectionId: this.sectionId } }));
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  #updateDrawerEmptyState() {
    const cartDrawer = this.closest('cart-drawer-component');
    if (cartDrawer && 'updateEmptyState' in cartDrawer && typeof cartDrawer.updateEmptyState === 'function') {
      cartDrawer.updateEmptyState(0);
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;

      return await response.json();
    } catch {
      return null;
    }
  }

  /** @param {CustomEvent<{productId?:string, variantId?:string}>} event */
  async #handleRecommendationAddToCart(event) {
    if (!this.isDrawer) return;

    const productId = event?.detail?.productId;
    const variantId = event?.detail?.variantId;

    if (!productId || !variantId) return;

    const cartPerformaceUpdateMarker =
      cartPerformance.createStartingMarker(`rec-add:user-action`);
    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll(
      'cart-items-component',
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    document
      .querySelectorAll('[data-cart-refresh-section-id]')
      .forEach((marker) => {
        if (!(marker instanceof HTMLElement)) return;
        const sectionId = marker.dataset.cartRefreshSectionId;
        if (sectionId) sectionsToUpdate.add(sectionId);
      });

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    formData.set('sections', Array.from(sectionsToUpdate).join(','));

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    const fetchCfg = fetchConfig('javascript', { body: formData });
    fetch(`${Theme.routes.cart_add_url}`, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        resetShimmer(this);

        if (response.errors) {
          this.dispatchEvent(
            new CartErrorEvent(
              'Mini Cart Product Rec',
              response.message,
              response.description,
              response.errors,
            ),
          );
          return;
        }

        const cart = await this.fetchCart();

        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.sectionId, {
            source: MINI_CART_REC_ADD_EVENT,
            itemCount: cart?.item_count || 0,
            productId,
            variantId,
            sections: response.sections,
          }),
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
